import { datadogRum } from '@datadog/browser-rum'
import { useEffect, useRef } from 'react'
import { CheckoutPayCreditCardModule } from '../lib/checkout-pay/CheckoutPayCreditCardModule'

export interface UseCkoFlowCardValidationParams {
  paymentModule: unknown
  isSdkInitialized?: boolean
  onValidityChange?: () => void
}

/** `validate()` 對非 CKO Flow 模組一律回 true，讓 caller 串接其他 SDK 驗證鏈。 */
export const useCkoFlowCardValidation = ({
  paymentModule,
  isSdkInitialized = true,
  onValidityChange,
}: UseCkoFlowCardValidationParams) => {
  const isValidRef = useRef(false)
  const onValidityChangeRef = useRef(onValidityChange)

  useEffect(() => {
    onValidityChangeRef.current = onValidityChange
  })

  useEffect(function subscribeCkoFlowValidation() {
    if (!isSdkInitialized) return
    if (!(paymentModule instanceof CheckoutPayCreditCardModule)) return
    isValidRef.current = false
    const unsubscribe = paymentModule.onValidationChange((isValid) => {
      onValidityChangeRef.current?.()
      isValidRef.current = isValid
    })
    return unsubscribe
  }, [isSdkInitialized, paymentModule])

  const validate = (): boolean => {
    if (!(paymentModule instanceof CheckoutPayCreditCardModule)) return true
    if (paymentModule.hasStoredCardAvailable()) {
      // stored_card validity 直接從 module 讀，不走 React state（避免 SDK callback 內觸發 Preact 崩潰）
      const isValid = paymentModule.isStoredCardValid()
      if (isValid) onValidityChangeRef.current?.()
      return isValid
    }
    if (isValidRef.current) return true
    // 呼叫 submit() 是為了讓 SDK 在 iframe 內標出未填欄位：欄位不完整時它只做本地驗證、
    // 不送出付款請求，建單取得的 payment session 尚未動用，使用者補齊後仍可用它付款。
    // 未掛載時 submit() 會 reject；此處上報而非靜默忽略，因為「應掛載而未掛載」本身即是需追蹤的異常。
    paymentModule.submit().catch((error) => {
      datadogRum.addError(error, { function: 'useCkoFlowCardValidation.validate' })
    })
    return false
  }

  return { validate }
}
